import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  AutoFixHigh as AutoFixHighIcon,
  ContentCopy as ContentCopyIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
  Save as SaveIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import locationService, { State } from '../../services/locationService';
import {
  useAdminInternalShipmentMutations,
  useAdminInternalShipmentRateCard,
} from '../../hooks/admin/useAdminInternalShipments';
import useInternalShipmentCountries from '../../hooks/useInternalShipmentCountries';
import {
  InternalShipmentRateClonePreview,
  InternalShipmentRateImportResult,
  InternalShipmentRateTier,
} from '../../services/internalShipmentService';
import internalShipmentService from '../../services/internalShipmentService';
import {
  INK,
  FONTS,
  primaryBtnSx,
  ghostBtnSx,
  ledgerInputSx,
  ledgerSwitchSx,
  ledgerChipSx,
  editorialDialogTitleSx,
  fieldLabelSx,
} from '../../styles/ledger';
import {
  LedgerPage,
  PageHeader,
  LedgerCard,
  SectionHeader,
  StatusDot,
} from '../../components/admin/LedgerPrimitives';

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  window.URL.revokeObjectURL(url);
};

const errMessage = (e: unknown, fallback: string) => {
  const m = (e as any)?.response?.data?.message;
  return typeof m === 'string' && m.trim() !== '' ? m : fallback;
};

const AdminInternalShipmentRatesPage: React.FC = () => {
  const [selectedState, setSelectedState] = useState<State | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [notes, setNotes] = useState('');
  const [tiers, setTiers] = useState<InternalShipmentRateTier[]>([]);
  const [feedback, setFeedback] = useState<{ severity: 'success' | 'error' | 'info'; message: string } | null>(null);
  const [importResult, setImportResult] = useState<InternalShipmentRateImportResult | null>(null);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [isDownloadingExport, setIsDownloadingExport] = useState(false);
  const [cloneSourceState, setCloneSourceState] = useState<State | null>(null);
  const [cloneTargetStates, setCloneTargetStates] = useState<State[]>([]);
  const [clonePreview, setClonePreview] = useState<InternalShipmentRateClonePreview | null>(null);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { saveRateCard, importRateCards, previewRateClone, cloneRateCards } = useAdminInternalShipmentMutations();
  const { recipientCountry, isLoading: isLoadingCountries, error: countryLookupError } = useInternalShipmentCountries();

  const statesQuery = useQuery({
    queryKey: ['internal-shipment-rate-states', recipientCountry?.id],
    queryFn: () => locationService.getStates({ country_id: recipientCountry?.id, per_page: 500 }),
    enabled: Boolean(recipientCountry?.id),
  });
  const states = useMemo(() => {
    const payload = statesQuery.data?.data as State[] | { data?: State[] } | undefined;
    return Array.isArray(payload) ? payload : payload?.data || [];
  }, [statesQuery.data]);

  const cloneTargetOptions = useMemo(
    () => states.filter((s) => s.id !== cloneSourceState?.id),
    [states, cloneSourceState?.id]
  );

  const { rateCardDetail, configuredRateCards, isLoading, detailQuery, listQuery } =
    useAdminInternalShipmentRateCard(selectedState?.id);
  const loadError = countryLookupError || statesQuery.error || listQuery.error || (selectedState ? detailQuery.error : null);

  useEffect(() => {
    if (!selectedState && states.length > 0) {
      const firstConfiguredStateId = configuredRateCards[0]?.stateId;
      const initialState = states.find((s) => s.id === firstConfiguredStateId) || states[0];
      setSelectedState(initialState);
    }
  }, [states, configuredRateCards, selectedState]);

  useEffect(() => {
    if (!cloneSourceState && selectedState) setCloneSourceState(selectedState);
  }, [cloneSourceState, selectedState]);

  useEffect(() => {
    if (!rateCardDetail) return;
    setIsActive(rateCardDetail.rateCard?.isActive || false);
    setNotes(rateCardDetail.rateCard?.notes || '');
    setTiers(
      rateCardDetail.rateCard?.tiers?.length
        ? rateCardDetail.rateCard.tiers
        : rateCardDetail.defaultTiers.map((t) => ({
            sortOrder: t.sortOrder,
            minWeightKg: t.minWeightKg,
            maxWeightKg: t.maxWeightKg,
            price: t.price || 0,
          }))
    );
  }, [rateCardDetail]);

  const handleTierPriceChange = (i: number, v: string) => {
    const parsed = Number(v);
    setTiers((cur) => cur.map((t, idx) => (idx === i ? { ...t, price: Number.isNaN(parsed) ? 0 : parsed } : t)));
  };

  const resetClonePreview = () => {
    setClonePreview(null);
    setCloneDialogOpen(false);
  };

  const handleSave = async () => {
    if (!selectedState) return;
    try {
      await saveRateCard.mutateAsync({ stateId: selectedState.id, isActive, notes, tiers });
      setFeedback({ severity: 'success', message: `Rates for ${selectedState.name} were saved.` });
    } catch (e) {
      setFeedback({ severity: 'error', message: errMessage(e, 'Failed to save rates.') });
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setIsDownloadingTemplate(true);
      const blob = await internalShipmentService.downloadInternalShipmentRatesTemplate();
      downloadBlob(blob, 'internal-shipment-rates-template.xlsx');
    } catch (e) {
      setFeedback({ severity: 'error', message: errMessage(e, 'Failed to download template.') });
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleDownloadExport = async () => {
    try {
      setIsDownloadingExport(true);
      const blob = await internalShipmentService.downloadInternalShipmentRatesExport();
      downloadBlob(blob, 'internal-shipment-rates-export.xlsx');
    } catch (e) {
      setFeedback({ severity: 'error', message: errMessage(e, 'Failed to export.') });
    } finally {
      setIsDownloadingExport(false);
    }
  };

  const handleImportFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const response = await importRateCards.mutateAsync(file);
      setImportResult(response.data);
      setFeedback({
        severity: response.data.summary.failedStates > 0 ? 'info' : 'success',
        message: response.message || 'Rates imported.',
      });
    } catch (err) {
      setImportResult(null);
      setFeedback({ severity: 'error', message: errMessage(err, 'Failed to import rates.') });
    } finally {
      e.target.value = '';
    }
  };

  const handlePreviewClone = async () => {
    if (!cloneSourceState || cloneTargetStates.length === 0) {
      setFeedback({ severity: 'info', message: 'Choose source + at least one target state.' });
      return;
    }
    try {
      const response = await previewRateClone.mutateAsync({
        sourceStateId: cloneSourceState.id,
        targetStateIds: cloneTargetStates.map((s) => s.id),
      });
      setClonePreview(response.data);
      setCloneDialogOpen(true);
    } catch (e) {
      setClonePreview(null);
      setCloneDialogOpen(false);
      setFeedback({ severity: 'error', message: errMessage(e, 'Failed to preview clone.') });
    }
  };

  const handleConfirmClone = async () => {
    if (!clonePreview) return;
    try {
      const response = await cloneRateCards.mutateAsync({
        sourceStateId: clonePreview.source.state.id,
        targetStateIds: clonePreview.targets.map((t) => t.state.id),
      });
      setClonePreview(response.data);
      setCloneDialogOpen(false);
      setCloneTargetStates([]);
      setFeedback({
        severity: 'success',
        message: `Cloned ${response.data.source.state.name} to ${response.data.summary.targetCount} states.`,
      });
    } catch (e) {
      setFeedback({ severity: 'error', message: errMessage(e, 'Failed to clone rates.') });
    }
  };

  return (
    <LedgerPage eyebrowFooter="Almaship · Internal shipment rates">
      <PageHeader
        eyebrow="Logistics · Rates"
        title={
          <>
            Shipment
            <Box component="span" sx={{ fontStyle: 'italic', color: INK.accent }}>
              {' '}rates.
            </Box>
          </>
        }
        description="Configure destination-state pricing bands for US recipients. Export, import, or clone in bulk."
        actions={
          <>
            <Button onClick={handleDownloadTemplate} disabled={isDownloadingTemplate} startIcon={<DescriptionIcon sx={{ fontSize: 16 }} />} sx={ghostBtnSx}>
              Template
            </Button>
            <Button onClick={handleDownloadExport} disabled={isDownloadingExport} startIcon={<DownloadIcon sx={{ fontSize: 16 }} />} sx={ghostBtnSx}>
              Export
            </Button>
            <Button onClick={() => fileInputRef.current?.click()} disabled={importRateCards.isPending} startIcon={<UploadFileIcon sx={{ fontSize: 16 }} />} sx={primaryBtnSx}>
              Import Excel
            </Button>
            <input ref={fileInputRef} type="file" hidden accept=".xlsx,.xls,.csv" onChange={handleImportFileChange} />
          </>
        }
      />

      {feedback && (
        <Alert
          severity={feedback.severity}
          sx={{ borderRadius: 0, mb: 3, fontFamily: FONTS.sans }}
          onClose={() => setFeedback(null)}
        >
          {feedback.message}
        </Alert>
      )}

      {importResult && (
        <Alert severity={importResult.summary.failedStates > 0 ? 'info' : 'success'} sx={{ borderRadius: 0, mb: 3, fontFamily: FONTS.sans }}>
          <Typography sx={{ fontFamily: FONTS.serif, fontSize: 16, color: INK.text, mb: 0.5 }}>
            Import summary
          </Typography>
          <Typography sx={{ fontSize: 13 }}>
            Rows processed: {importResult.summary.processedRows}. Successful: {importResult.summary.successfulStates}. Failed: {importResult.summary.failedStates}.
          </Typography>
          {importResult.failures.length > 0 && (
            <List dense sx={{ mt: 1 }}>
              {importResult.failures.slice(0, 5).map((f, i) => (
                <ListItem key={`${f.state}-${i}`} disableGutters>
                  <ListItemText
                    primary={
                      <Typography sx={{ fontFamily: FONTS.sans, fontSize: 13, color: INK.text }}>
                        {f.state} · rows {f.rows.join(', ')}
                      </Typography>
                    }
                    secondary={<Typography sx={{ fontSize: 12, color: INK.muted }}>{f.message}</Typography>}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '380px 1fr' }, gap: 2 }}>
        <Stack spacing={2}>
          <LedgerCard>
            <SectionHeader eyebrow="Selector" title="State picker" description="Only US destination states." />
            <Autocomplete
              options={states}
              loading={isLoadingCountries || statesQuery.isLoading}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={selectedState}
              onChange={(_, v) => setSelectedState(v)}
              renderInput={(p) => <TextField {...p} placeholder="Select state" size="small" sx={ledgerInputSx} />}
            />
            <Box sx={{ mt: 2 }}>
              <Typography sx={fieldLabelSx}>Configured states</Typography>
              <Box sx={{ border: `1px solid ${INK.hairline}`, borderRadius: 0.5, overflow: 'hidden' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        '& .MuiTableCell-head': {
                          fontFamily: FONTS.mono,
                          fontSize: 10,
                          letterSpacing: '0.14em',
                          textTransform: 'uppercase',
                          color: INK.subtle,
                          fontWeight: 500,
                          bgcolor: INK.surfaceTint,
                          borderBottom: `1px solid ${INK.hairline}`,
                          py: 1,
                        },
                      }}
                    >
                      <TableCell>State</TableCell>
                      <TableCell>Active</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {configuredRateCards.length > 0 ? (
                      configuredRateCards.map((card) => (
                        <TableRow
                          key={card.id}
                          selected={selectedState?.id === card.stateId}
                          onClick={() => {
                            const s = states.find((it) => it.id === card.stateId);
                            if (s) setSelectedState(s);
                          }}
                          sx={{
                            cursor: 'pointer',
                            '& .MuiTableCell-root': { borderBottom: `1px solid ${INK.hairlineSoft}`, py: 1.25, fontFamily: FONTS.sans, fontSize: 13 },
                            '&:hover': { bgcolor: INK.surfaceTint },
                            '&.Mui-selected': { bgcolor: INK.accentSoft, '&:hover': { bgcolor: INK.accentSoft } },
                            '&:last-of-type .MuiTableCell-root': { borderBottom: 'none' },
                          }}
                        >
                          <TableCell>{card.state?.name || `State ${card.stateId}`}</TableCell>
                          <TableCell>
                            <StatusDot label={card.isActive ? 'On' : 'Off'} tone={card.isActive ? 'active' : 'inactive'} />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={2} sx={{ p: 2, fontFamily: FONTS.sans, fontSize: 12, color: INK.subtle, borderBottom: 'none' }}>
                          No configured states yet.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          </LedgerCard>

          <LedgerCard>
            <SectionHeader
              eyebrow="Clone studio"
              title="Bulk clone"
              description="Copy tiers from one state to many."
              action={
                <Button
                  size="small"
                  onClick={() => { setCloneSourceState(selectedState); setClonePreview(null); }}
                  disabled={!selectedState}
                  sx={ghostBtnSx}
                  startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                >
                  Use editor
                </Button>
              }
            />
            <Stack spacing={2}>
              <Box>
                <Typography sx={fieldLabelSx}>Source state</Typography>
                <Autocomplete
                  options={states}
                  getOptionLabel={(o) => o.name}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={cloneSourceState}
                  onChange={(_, v) => {
                    setCloneSourceState(v);
                    setCloneTargetStates((cur) => cur.filter((s) => s.id !== v?.id));
                    resetClonePreview();
                  }}
                  renderInput={(p) => <TextField {...p} size="small" placeholder="Choose source" sx={ledgerInputSx} />}
                />
              </Box>
              <Box>
                <Typography sx={fieldLabelSx}>Target states</Typography>
                <Autocomplete
                  multiple
                  options={cloneTargetOptions}
                  getOptionLabel={(o) => o.name}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={cloneTargetStates}
                  onChange={(_, v) => { setCloneTargetStates(v); resetClonePreview(); }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip {...getTagProps({ index })} key={option.id} label={option.name} size="small" sx={ledgerChipSx} />
                    ))
                  }
                  renderInput={(p) => <TextField {...p} size="small" placeholder="Choose one or more" sx={ledgerInputSx} />}
                />
              </Box>
              {clonePreview && (
                <Alert severity="info" sx={{ borderRadius: 0, fontFamily: FONTS.sans, border: `1px solid ${INK.hairline}` }}>
                  {clonePreview.summary.createCount} create · {clonePreview.summary.updateCount} update
                </Alert>
              )}
              <Button
                onClick={handlePreviewClone}
                disabled={previewRateClone.isPending || cloneRateCards.isPending || !cloneSourceState || cloneTargetStates.length === 0}
                sx={primaryBtnSx}
                startIcon={<AutoFixHighIcon sx={{ fontSize: 16 }} />}
              >
                Preview clone plan
              </Button>
            </Stack>
          </LedgerCard>
        </Stack>

        <LedgerCard>
          {loadError ? (
            <Alert severity="error" sx={{ borderRadius: 0, fontFamily: FONTS.sans }}>
              Failed to load rate data.
            </Alert>
          ) : !selectedState && !statesQuery.isLoading && !isLoadingCountries ? (
            <Alert severity="info" sx={{ borderRadius: 0, fontFamily: FONTS.sans }}>
              No states available for configuration.
            </Alert>
          ) : !selectedState || isLoading || isLoadingCountries ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={28} sx={{ color: INK.accent }} />
            </Box>
          ) : rateCardDetail ? (
            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'flex-end' }} spacing={2}>
                <Box>
                  <Typography sx={{ fontFamily: FONTS.serif, fontSize: 28, color: INK.text, letterSpacing: '-0.015em' }}>
                    {selectedState.name}
                  </Typography>
                  <Typography sx={{ fontFamily: FONTS.sans, fontSize: 13, color: INK.muted, mt: 0.25 }}>
                    Configure the price for each weight tier.
                  </Typography>
                </Box>
                <FormControlLabel
                  control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} sx={ledgerSwitchSx} />}
                  label={
                    <Typography sx={{ fontFamily: FONTS.sans, fontSize: 13, color: INK.text }}>
                      {isActive ? 'Rates active' : 'Rates inactive'}
                    </Typography>
                  }
                />
              </Stack>

              <Box>
                <Typography sx={fieldLabelSx}>Notes</Typography>
                <TextField
                  multiline
                  minRows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  fullWidth
                  size="small"
                  sx={ledgerInputSx}
                />
              </Box>

              <Box sx={{ border: `1px solid ${INK.hairline}`, borderRadius: 0.5, overflow: 'hidden' }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow
                        sx={{
                          '& .MuiTableCell-head': {
                            fontFamily: FONTS.mono,
                            fontSize: 10,
                            letterSpacing: '0.14em',
                            textTransform: 'uppercase',
                            color: INK.subtle,
                            fontWeight: 500,
                            bgcolor: INK.surfaceTint,
                            borderBottom: `1px solid ${INK.hairline}`,
                            py: 1.25,
                          },
                        }}
                      >
                        <TableCell>Tier</TableCell>
                        <TableCell>Min kg</TableCell>
                        <TableCell>Max kg</TableCell>
                        <TableCell>Price</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tiers.map((t, i) => (
                        <TableRow
                          key={`${t.sortOrder}-${t.minWeightKg}`}
                          sx={{
                            '& .MuiTableCell-root': {
                              borderBottom: `1px solid ${INK.hairlineSoft}`,
                              py: 1.25,
                              fontFamily: FONTS.mono,
                              fontSize: 12.5,
                              color: INK.text,
                            },
                            '&:last-of-type .MuiTableCell-root': { borderBottom: 'none' },
                          }}
                        >
                          <TableCell>
                            {t.minWeightKg} – {t.maxWeightKg ?? '∞'} kg
                          </TableCell>
                          <TableCell>{t.minWeightKg}</TableCell>
                          <TableCell>{t.maxWeightKg ?? 'Open'}</TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              value={t.price}
                              onChange={(e) => handleTierPriceChange(i, e.target.value)}
                              inputProps={{ min: 0, step: 0.01 }}
                              size="small"
                              sx={{ ...ledgerInputSx, width: 120 }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box>
                <Button
                  onClick={handleSave}
                  disabled={saveRateCard.isPending || importRateCards.isPending}
                  sx={primaryBtnSx}
                  startIcon={<SaveIcon sx={{ fontSize: 16 }} />}
                >
                  Save rates
                </Button>
              </Box>
            </Stack>
          ) : (
            <Alert severity="info" sx={{ borderRadius: 0, fontFamily: FONTS.sans }}>
              Select a state to manage rate tiers.
            </Alert>
          )}
        </LedgerCard>
      </Box>

      <Dialog
        open={cloneDialogOpen && Boolean(clonePreview)}
        onClose={() => setCloneDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 0.5 } }}
      >
        <DialogTitle sx={editorialDialogTitleSx}>Confirm clone</DialogTitle>
        <DialogContent dividers>
          {clonePreview && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Alert severity="info" sx={{ borderRadius: 0, fontFamily: FONTS.sans, border: `1px solid ${INK.hairline}` }}>
                {clonePreview.source.state.name} will be copied to {clonePreview.summary.targetCount} states. Nothing changes until you confirm.
              </Alert>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={`Creates ${clonePreview.summary.createCount}`} sx={ledgerChipSx} />
                <Chip label={`Updates ${clonePreview.summary.updateCount}`} sx={ledgerChipSx} />
              </Stack>
              <List dense>
                {clonePreview.targets.map((target) => (
                  <ListItem
                    key={target.state.id}
                    sx={{ borderBottom: `1px solid ${INK.hairlineSoft}`, py: 1.5 }}
                    secondaryAction={
                      <Typography sx={{ fontFamily: FONTS.mono, fontSize: 10.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: target.action === 'create' ? INK.accent : INK.warning, fontWeight: 600 }}>
                        {target.action}
                      </Typography>
                    }
                  >
                    <ListItemText
                      primary={<Typography sx={{ fontFamily: FONTS.sans, fontSize: 13, color: INK.text, fontWeight: 500 }}>{target.state.name}</Typography>}
                      secondary={<Typography sx={{ fontFamily: FONTS.sans, fontSize: 11.5, color: INK.muted }}>{target.actionLabel}</Typography>}
                    />
                  </ListItem>
                ))}
              </List>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={() => setCloneDialogOpen(false)} sx={ghostBtnSx}>
            Back
          </Button>
          <Button onClick={handleConfirmClone} disabled={cloneRateCards.isPending} sx={primaryBtnSx} startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}>
            Clone rates
          </Button>
        </DialogActions>
      </Dialog>
    </LedgerPage>
  );
};

export default AdminInternalShipmentRatesPage;
